// Setup → Agency's Team claims panel (finance-real, the user 2026-10-22): the most the team can claim from a job, as a
// percent of its price, 84% unless changed. Each job's margin % caps its claims, and this caps every job's: a job's margin
// % never goes below 100 less it. Changing it moves every job's ceiling at once; claims already over a lower one stay,
// and their jobs read over the cap. A WorkspacePanel like every Setup panel (DD-002, DD-047), whose one action opens the
// drawer setting it (DD-005, DD-051), `modal=claim-cap`.
import { useState } from "react";
import { EditIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { FormField, inputClassName } from "@/components/ui/FormField";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { ModalPending } from "../components/ModalPending";
import { useSubmitOnce } from "../hooks/useSubmitOnce";
import type { ClaimSettings } from "../types";
import { closeUnlessPending } from "../utils/mutations";
import { useClaimCapCard } from "./hooks/useClaimCapCard";
import { useClaimCapDrawer, type ClaimCapDrawerModel } from "./hooks/useClaimCapDrawer";

const LABEL = "Team claims";
const jobWord = ENTITY_CONFIG.contract.label.toLowerCase();

export function ClaimCapCard({ onEdit }: { onEdit: () => void }) {
  const { settings } = useClaimCapCard();
  const pct = settings.data?.maxClaimPct;
  return (
    <WorkspacePanel
      title={LABEL}
      collapsed={false}
      loading={settings.isLoading}
      className="h-full"
      actions={<PanelHeaderAction label="Change" icon={<EditIcon className="w-4 h-4" />} onClick={onEdit} disabled={!settings.data} />}
    >
      <div className="space-y-2 text-sm">
        {settings.isError && <p className="text-red-600">Could not load the team's claims setting.</p>}
        {pct !== undefined && (
          <div>
            <p className="text-xs text-gray-500">{`Most the team can claim from a ${jobWord}`}</p>
            <p className="mt-1 text-xl font-semibold tabular-nums text-gray-900">{`${pct}%`}</p>
          </div>
        )}
        <p className="text-xs text-gray-500">
          {pct !== undefined
            ? `Each ${jobWord}'s margin % caps what its claims come to, and is never below ${100 - pct}%.`
            : `Each ${jobWord}'s margin % caps what its claims come to, and never goes below what this leaves.`}
        </p>
      </div>
    </WorkspacePanel>
  );
}

export function ClaimCapDrawer({ onClose }: { onClose: () => void }) {
  const { settings, save } = useClaimCapDrawer();
  if (!settings.data) {
    return <ModalPending title={LABEL} onClose={onClose} failure={settings.isError ? "Could not load the team's claims setting." : null} retrying={settings.isFetching} retry={() => void settings.refetch()} />;
  }
  return <ClaimCapForm settings={settings.data} save={save} onClose={onClose} />;
}

function ClaimCapForm({ settings, save, onClose }: { settings: ClaimSettings; save: ClaimCapDrawerModel["save"]; onClose: () => void }) {
  const send = useSubmitOnce();
  const [value, setValue] = useState(String(settings.maxClaimPct));
  const n = Number(value);
  const valid = value.trim() !== "" && Number.isInteger(n) && n >= 1 && n <= 100;
  return (
    <FinanceDrawer
      onClose={closeUnlessPending(save.isPending, onClose)}
      title={`Change · ${LABEL}`}
      footer={
        <Button disabled={!valid || save.isPending} isLoading={save.isPending} onClick={() => send(() => save.mutateAsync({ maxClaimPct: n }), () => onClose())}>
          Save
        </Button>
      }
    >
      <FormField
        label={`Most the team can claim from a ${jobWord}`}
        hint={valid ? `A ${jobWord}'s margin % is then at least ${100 - n}%. Claims already over it stay, and read over the cap.` : undefined}
        error={valid ? undefined : "A whole number from 1 to 100"}
      >
        <span className="relative block w-28">
          <input type="number" min={1} max={100} step={1} aria-label="Most the team can claim" className={`${inputClassName} pr-6 text-right`} value={value} onChange={(e) => setValue(e.target.value)} />
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
        </span>
      </FormField>
    </FinanceDrawer>
  );
}
